import { store } from '@lotta/self-service/lottery-fe/data-access-state';
import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@lotta/self-service/lottery-fe/ui-common';

import App from './app/App';
import { environment } from './environments/environment';
import './i18n';

const { AUTH0_CLIENT_ID, AUTH0_DOMAIN, AUTH0_AUDIENCE } = environment;

/**
 * If third party cookies are disabled we end up in an endless redirect loop
 * between auth0 and our app. We try to detect this loop by counting how many
 * times we returned to our app in the `onRedirectCallback`-function.
 * @see https://auth0.com/docs/troubleshoot/authentication-issues/renew-tokens-when-using-safari
 *
 * Possible solutions
 *
 * - Use custom domain for auth0.
 * @see https://auth0.com/docs/customize/custom-domains
 *
 * - Store tokens in localstorage instead of cookies
 * @see https://auth0.com/docs/libraries/auth0-single-page-app-sdk#change-storage-options
 */
const redirectCounter = {
  count: parseInt(sessionStorage.getItem('login-redirect-count') ?? '0', 10) || 0,
  reset: () => sessionStorage.removeItem('login-redirect-count'),
};

const onRedirectCallback = (appState: AppState | undefined) => {
  if (appState?.['count']) {
    sessionStorage.setItem('login-redirect-count', '' + appState?.['count']);
  }
  window.location.replace(appState?.returnTo || window.location.pathname);
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <StrictMode>
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: AUTH0_AUDIENCE,
        redirect_uri: window.location.origin,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <BrowserRouter>
        <Provider store={store}>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <App redirectCounter={redirectCounter} />
          </ErrorBoundary>
        </Provider>
      </BrowserRouter>
    </Auth0Provider>
  </StrictMode>
);
